import { AsyncBoundary, Card, Chip, KpiCard } from '@kollez/design-system';
import { formatIndianDate } from '@kollez/shared/format';
import { useNexieAudit } from '@/lib/queries';

/**
 * NexieAudit (F-079) — every Nexie-authored action, timestamped and grouped.
 *
 * Reads AuditLog rows where actor.role='nexie' or action starts with 'nexie.'. Tenant-scoped.
 * Prevents the "black-box AI" concern: every draft, every send, every match-explain call is
 * on the record with who triggered it, when, and what subject it touched.
 */

function actionLabel(a: string): string {
  const parts = a.split('.');
  if (parts[0] !== 'nexie') return a;
  return parts.slice(1).join(' · ').replace(/_/g, ' ');
}

function actionTone(a: string): 'info' | 'success' | 'default' | 'warning' {
  if (a.includes('sent')) return 'success';
  if (a.includes('discard')) return 'default';
  if (a.includes('draft')) return 'info';
  if (a.includes('flag') || a.includes('cap')) return 'warning';
  return 'info';
}

export default function NexieAudit() {
  const q = useNexieAudit(100);
  const rows = q.data?.rows ?? [];
  const summary = q.data?.summary;

  return (
    <>
      <header className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <div className="eyebrow">Nexie · Audit trail</div>
          <h1>What Nexie did</h1>
          <p className="sub">
            Every AI-authored action inside your tenant. Read-only. Rows here are the same records
            compliance auditors see — no summarisation, no filtering by intent.
          </p>
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Pending drafts" value={String(summary?.draftCount ?? '—')} />
        <KpiCard label="Sent drafts" value={String(summary?.sentCount ?? '—')} />
        <KpiCard label="Audit rows shown" value={String(summary?.count ?? '—')} />
      </div>

      <AsyncBoundary
        kind="table"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && rows.length === 0}
        emptyTitle="No Nexie activity yet"
        emptyHint="Nexie activity shows up here as soon as a draft is generated, sent, or discarded."
      >
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table className="tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 170 }}>When</th>
                  <th>Action</th>
                  <th className="hide-mobile">Actor</th>
                  <th className="hide-mobile">Subject</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td style={{ fontSize: 12, color: 'rgb(var(--ink-tertiary))', whiteSpace: 'nowrap' }}>
                      {formatIndianDate(new Date(r.at))}
                      <div style={{ fontSize: 11 }}>{new Date(r.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td>
                      <Chip tone={actionTone(r.action)}>{actionLabel(r.action)}</Chip>
                    </td>
                    <td className="hide-mobile" style={{ fontSize: 13 }}>
                      {r.actor?.role === 'nexie' ? 'Nexie' : (r.actor?.role ?? '—')}
                    </td>
                    <td className="hide-mobile" style={{ fontSize: 13, color: 'rgb(var(--ink-tertiary))' }}>
                      {r.subject?.kind ? `${r.subject.kind}${r.subject.id ? ` · ${String(r.subject.id).slice(-6)}` : ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </AsyncBoundary>
    </>
  );
}
